import React, { useState, useEffect } from 'react'
import Avatar from '@atlaskit/avatar'
import Lozenge from '@atlaskit/lozenge'
import Tooltip from '@atlaskit/tooltip'
import MoreIcon from '@atlaskit/icon/glyph/more'
import DragHandlerIcon from '@atlaskit/icon/glyph/drag-handler'
import DocumentIcon from '@atlaskit/icon/glyph/document'
import Button from '@atlaskit/button'
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu'
import { getUserDetails, getCachedUserDetails } from '../../utils/userCache'

/**
 * SCENARIO ROW COMPONENT (Backlog-style)
 *
 * Displays a test scenario as a flat row in the folder browser,
 * matching Jira's backlog view density and style.
 *
 * Features:
 * - Single-line layout: name left-aligned, metadata right-aligned
 * - Flat row with bottom divider only (no card borders/shadows)
 * - Subtle background change on hover
 * - Click to navigate to scenario detail page
 */
function ScenarioCard({
  scenario,
  onClick,
  onEdit,
  onMove,
  onDelete,
  onClone,
  dragHandleProps,
  // When true, the handle container is still rendered (to keep the left spacing consistent
  // across cards), but the icon is invisible, the cursor is default, and drag listeners
  // are not attached. Set by the parent panel when the visible list does not map 1:1 to
  // the DB's folder-scoped orderIndex (All Scenarios, active filters, or non-default sort).
  isDragDisabled = false,
  isDragging = false
}) {
  // Extract scenario data
  const {
    id,
    scenarioKey,  // Jira-style key (e.g., "POJ-1")
    scenarioName,
    description,
    priority = 'Medium',
    assignee,
    runs = [],
    labels = [],
    testCaseCount = 0,
    createdOn
  } = scenario

  const [isHovered, setIsHovered] = useState(false)
  // Track dropdown menu open state to prevent menu from disappearing
  // when mouse briefly leaves the row while navigating to dropdown items
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  // Initialize with cached value for instant render if available
  const [userInfo, setUserInfo] = useState(() => {
    if (assignee && typeof assignee === 'object' && assignee.displayName) {
      return assignee
    }
    if (assignee && typeof assignee === 'string') {
      return getCachedUserDetails(assignee)
    }
    return null
  })

  // Fetch user details when assignee is an account ID string
  // Uses cache-first approach - parent panel prefetches users in bulk
  useEffect(() => {
    // If assignee is already an object with displayName, use it directly
    if (assignee && typeof assignee === 'object' && assignee.displayName) {
      setUserInfo(assignee)
      return
    }
    // If assignee is a string (account ID), fetch from cache or API
    if (assignee && typeof assignee === 'string') {
      getUserDetails(assignee).then(user => {
        if (user) {
          setUserInfo(user)
        } else {
          setUserInfo({ accountId: assignee, displayName: 'Unknown User', avatarUrl: null })
        }
      }).catch(() => {
        setUserInfo({ accountId: assignee, displayName: 'Unknown User', avatarUrl: null })
      })
    } else {
      // No assignee - unassigned
      setUserInfo(null)
    }
  }, [assignee])

  // Calculate aggregated stats from all runs - tracks all status types
  const calculateAggregatedStats = () => {
    let passCount = 0
    let failCount = 0
    let blockedCount = 0
    let queryCount = 0
    let excludeCount = 0

    runs.forEach(run => {
      if (run.statusCounts) {
        passCount += run.statusCounts.Pass || 0
        failCount += run.statusCounts.Fail || 0
        blockedCount += run.statusCounts.Blocked || 0
        queryCount += run.statusCounts.Query || 0
        excludeCount += run.statusCounts.Exclude || 0
      }
    })

    const total = passCount + failCount + blockedCount + queryCount + excludeCount
    return { passCount, failCount, blockedCount, queryCount, excludeCount, total }
  }

  const stats = calculateAggregatedStats()
  const passPercent = stats.total > 0 ? (stats.passCount / stats.total) * 100 : 0
  const failPercent = stats.total > 0 ? (stats.failCount / stats.total) * 100 : 0
  const blockedPercent = stats.total > 0 ? (stats.blockedCount / stats.total) * 100 : 0
  const queryPercent = stats.total > 0 ? (stats.queryCount / stats.total) * 100 : 0
  const excludePercent = stats.total > 0 ? (stats.excludeCount / stats.total) * 100 : 0

  // Status colors for progress bar segments
  const statusColors = {
    pass: 'var(--ds-background-success-bold, #36B37E)',
    fail: 'var(--ds-background-danger-bold, #DE350B)',
    blocked: 'var(--ds-icon-warning, #E06C00)',
    query: 'var(--ds-background-information-bold, #0065FF)',
    exclude: 'var(--ds-background-neutral-bold, #6B778C)'
  }

  // Get priority lozenge appearance
  const getPriorityAppearance = (priority) => {
    switch (priority) {
      case 'Critical':
        return 'removed'
      case 'High':
        return 'moved'
      case 'Medium':
        return 'inprogress'
      case 'Low':
        return 'new'
      default:
        return 'default'
    }
  }

  return (
    <div
      className="scenario-card"
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        /* Flat backlog row: no border-radius, no box border, only bottom divider */
        backgroundColor: (isHovered || isMenuOpen)
          ? 'var(--ds-background-neutral-subtle-hovered, #F4F5F7)'
          : 'var(--ds-surface, #FFFFFF)',
        borderRadius: '0',
        border: 'none',
        borderBottom: '1px solid var(--ds-border, #DFE1E6)',
        boxShadow: 'none',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease'
      }}
    >
      {/* Single-row layout: drag handle + content all on one line */}
      <div style={{ padding: '6px 12px 6px 0', display: 'flex', gap: '0', alignItems: 'center' }}>
        {/* Drag Handle — flush left, appears on hover when drag is enabled.
            When isDragDisabled is true, we still render this container so the left
            spacing stays identical to draggable cards, but we omit the drag listeners
            and force the icon to stay invisible (no hover reveal). */}
        {dragHandleProps && (
          <div
            {...(isDragDisabled ? {} : dragHandleProps)}
            className="drag-handle"
            style={{
              cursor: isDragDisabled ? 'default' : (isDragging ? 'grabbing' : 'grab'),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: !isDragDisabled && (isHovered || isMenuOpen) ? 1 : 0,
              transition: 'opacity 0.15s ease',
              touchAction: 'none',
              padding: '0 2px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <DragHandlerIcon
              label="Drag to reorder"
              size="medium"
              primaryColor="var(--ds-text-subtlest, #6B778C)"
            />
          </div>
        )}

        {/* Single Row: [icon] [key] [name...] | [progress] [priority] [avatar] [counts] [actions] */}
        <div style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {/* Scenario type icon */}
          <DocumentIcon
            label="Scenario"
            size="small"
            primaryColor="var(--ds-icon-brand, #0052CC)"
          />

          {/* Scenario Key (if available) - uses --ds-font-body token with monospace override */}
          {scenarioKey && (
            <span style={{
              font: 'var(--ds-font-body)',
              fontFamily: 'monospace',
              fontWeight: 500,
              letterSpacing: '-0.003em',
              color: 'var(--ds-text-subtlest, #6B778C)',
              flexShrink: 0
            }}>
              {scenarioKey}
            </span>
          )}

          {/* Scenario Name - uses --ds-font-body token to match details page typography */}
          <span style={{
            flex: 1,
            font: 'var(--ds-font-body)',
            fontWeight: 500,
            letterSpacing: '-0.003em',
            color: 'var(--ds-text, #172B4D)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {scenarioName}
          </span>

          {/* Right-aligned metadata section */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexShrink: 0,
            marginLeft: 'auto'
          }}>
            {/* Compact Progress Bar with tooltip - fixed-width container for alignment */}
            <div style={{ width: '160px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            {stats.total > 0 && (
              <Tooltip
                content={
                  <div style={{ padding: '4px' }}>
                    {stats.passCount > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: statusColors.pass }} />
                        Pass: {stats.passCount} ({Math.round(passPercent)}%)
                      </div>
                    )}
                    {stats.failCount > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: statusColors.fail }} />
                        Fail: {stats.failCount} ({Math.round(failPercent)}%)
                      </div>
                    )}
                    {stats.blockedCount > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: statusColors.blocked }} />
                        Blocked: {stats.blockedCount} ({Math.round(blockedPercent)}%)
                      </div>
                    )}
                    {stats.queryCount > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: statusColors.query }} />
                        Query: {stats.queryCount} ({Math.round(queryPercent)}%)
                      </div>
                    )}
                    {stats.excludeCount > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: statusColors.exclude }} />
                        Exclude: {stats.excludeCount} ({Math.round(excludePercent)}%)
                      </div>
                    )}
                    <div style={{ marginTop: '4px', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '4px' }}>
                      Total: {stats.total} across {runs.length} run{runs.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                }
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {/* Compact progress bar: 120px wide, 4px tall */}
                  <div style={{
                    width: '120px',
                    height: '4px',
                    borderRadius: '2px',
                    backgroundColor: 'var(--ds-background-neutral, #DFE1E6)',
                    overflow: 'hidden',
                    display: 'flex'
                  }}>
                    <div style={{ width: `${passPercent}%`, height: '100%', backgroundColor: statusColors.pass }} />
                    <div style={{ width: `${failPercent}%`, height: '100%', backgroundColor: statusColors.fail }} />
                    <div style={{ width: `${blockedPercent}%`, height: '100%', backgroundColor: statusColors.blocked }} />
                    <div style={{ width: `${queryPercent}%`, height: '100%', backgroundColor: statusColors.query }} />
                    <div style={{ width: `${excludePercent}%`, height: '100%', backgroundColor: statusColors.exclude }} />
                  </div>
                  <span style={{
                    fontSize: '11px',
                    fontWeight: 500,
                    color: 'var(--ds-text-subtle, #5E6C84)',
                    minWidth: '28px'
                  }}>
                    {Math.round(passPercent)}%
                  </span>
                </div>
              </Tooltip>
            )}
            </div>

            {/* Priority lozenge - fixed-width container for alignment */}
            <div style={{ width: '70px', flexShrink: 0, display: 'flex', justifyContent: 'flex-start' }}>
              <Lozenge appearance={getPriorityAppearance(priority)}>
                {priority}
              </Lozenge>
            </div>

            {/* Assignee avatar with tooltip */}
            <Tooltip content={userInfo?.displayName || 'Unassigned'}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Avatar
                  size="xsmall"
                  src={userInfo?.avatarUrl || null}
                  name={userInfo?.displayName || 'Unassigned'}
                />
              </div>
            </Tooltip>

            {/* Test case count - fixed-width for alignment */}
            <span style={{
              fontSize: '11px',
              color: 'var(--ds-text-subtlest, #6B778C)',
              width: '52px',
              textAlign: 'left',
              flexShrink: 0
            }}>
              {testCaseCount} {testCaseCount !== 1 ? 'cases' : 'case'}
            </span>

            {/* Run count - fixed-width for alignment */}
            <span style={{
              fontSize: '11px',
              color: 'var(--ds-text-subtlest, #6B778C)',
              width: '42px',
              textAlign: 'left',
              flexShrink: 0
            }}>
              {runs.length} {runs.length !== 1 ? 'runs' : 'run'}
            </span>
          </div>

          {/* Actions Menu - visible on hover */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              opacity: (isHovered || isMenuOpen) ? 1 : 0,
              transition: 'opacity 0.15s ease',
              pointerEvents: (isHovered || isMenuOpen) ? 'auto' : 'none',
              flexShrink: 0
            }}
          >
            <DropdownMenu
              trigger={({ triggerRef, ...props }) => (
                <Button
                  {...props}
                  ref={triggerRef}
                  appearance="subtle"
                  spacing="compact"
                  iconBefore={<MoreIcon label="More actions" size="small" />}
                />
              )}
              shouldRenderToParent
              onOpenChange={({ isOpen }) => setIsMenuOpen(isOpen)}
            >
              <DropdownItemGroup>
                <DropdownItem onClick={() => onEdit?.(id)}>Edit</DropdownItem>
                <DropdownItem onClick={() => onClone?.(id)}>Clone</DropdownItem>
                <DropdownItem onClick={() => onMove?.(id)}>Move to Folder</DropdownItem>
                <DropdownItem onClick={() => onDelete?.(id)}>Delete</DropdownItem>
              </DropdownItemGroup>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </div>
  )
}

export { ScenarioCard }
